import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
	Box,
	Button,
	Grid,
	IconButton,
	InputAdornment,
	Paper,
	Typography,
} from "@mui/material";
import axios from "axios";
import AddIcon from "@mui/icons-material/Add";

import { RetryImage } from "../../../components/RetryImage";

import { Input } from "../../../components/Input";
import { useGlobalSnackBar } from "../../SnackbarContext";
import { createModel, getModelByID, updateModel } from "../../../apis/model";
import { deleteFile } from "../../../apis/fileUpload";
import { SelectTag } from "../../../components/Tag/SelectTab";
import { useOptionData } from "../../../hook/useOptionData";
import {
	transformOptions,
	transformOriginalOptions,
	Option,
} from "../../../utils/option";
import { Loader } from "../../../components/Loader";
import { CustomDialog } from "../../../components/modal/CustomDialog";

import "../Options/styles.css"
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { useBreadcrumb } from "../../../context/BreadcrumbContext";

export const ModelsView: React.FC = () => {
	const { ID } = useParams();
	const navigate = useNavigate();

	const { options } = useOptionData();

	const [model, setModel] = useState<any>(null);
	const [logos, setLogos] = useState<Array<string | ArrayBuffer | null>>([
		null,
		null,
		null,
	]);
	const [tags, setTags] = useState([]);
	const [isLoading, setLoading] = useState<boolean>(true);
	const [openModal, setOpenModal] = useState<boolean>(false);
	const [imageData, setImageData] = useState<{ logo: any; index: number }>();
	const { showSnackBar } = useGlobalSnackBar();
	const currentUser = useSelector((state: RootState) => state.adminAuth.admin);
	const { setDetail, clearDetail } = useBreadcrumb();

	useEffect(() => {
		return () => clearDetail();
	}, [clearDetail]);

	useEffect(() => {
		const fetchModelData = async () => {
			try {
				const modelData = await getModelByID({ ID });
				setModel({ ...modelData });
				setLogos(modelData.images ? JSON.parse(modelData.images) : []);
				setTags(
					modelData.applicable_option_ids
						? JSON.parse(modelData.applicable_option_ids)
						: []
				);
				if (modelData.model_name) {
					setDetail(modelData.model_name, "/models");
				}
				setLoading(false);
			} catch (error) {
				showSnackBar("Failed to fetch model", "error");
			}
		};

		if (ID) {
			fetchModelData();
		} else {
			setLoading(false);
		}
	}, [ID, showSnackBar]);

	const permissionErrorMessage = () => {
		showSnackBar("Invalid operation: Only Administrators can make this change.", "error");
	};

	const handleUploadClick = (
		event: React.ChangeEvent<HTMLInputElement>,
		index: number
	) => {
		if (currentUser?.permission !== "Administrator") {
			permissionErrorMessage();
			return;
		}

		const file = event.target.files?.[0];
		if (!file) return;

		const formData = new FormData();
		formData.append("img", file);

		axios
			.post(process.env.REACT_APP_UPLOAD_URL || "", formData, {
				headers: {
					"Content-Type": "multipart/form-data",
					Authorization: "Bearer " + localStorage.getItem("admin_access_token"),
				},
			})
			.then((response) => {
				const { filename } = response.data;
				setLogos((prevLogos) => {
					const updatedLogos = [...prevLogos];
					updatedLogos[index] = filename;
					return updatedLogos;
				});
			})
			.catch((error) => {
				console.error("Error uploading file:", error);
			});
	};

	const handleRemoveClick = async (
		logo: string | ArrayBuffer | null,
		index: number
	) => {
		if (currentUser?.permission !== "Administrator") {
			permissionErrorMessage();
			return;
		}

		if (!logo) return;
		setOpenModal(true);
		setImageData({ logo: logo, index: index });
	};

	const handleDelete = async () => {
		if (imageData?.logo) {
			await deleteFile({ file: logos[imageData.index] });
			setLogos((prevLogos) => {
				const updatedLogos = [...prevLogos];
				updatedLogos[imageData.index] = null;
				return updatedLogos;
			});
			setOpenModal(false);
		}
	};

	const handleSubmit = async (event: any) => {
		console.log(event.target);
		event.preventDefault();

		if (currentUser?.permission !== "Administrator") {
			permissionErrorMessage();
			return;
		}

		const formData = new FormData(event.target);
		const formValues: any = Object.fromEntries(formData.entries());
		formValues["images"] = JSON.stringify(logos);
		formValues["ID"] = ID;
		formValues["applicable_option_ids"] = JSON.stringify(tags);
		for (const key in formValues) {
			if (formValues[key] === "") {
				delete formValues[key];
			}
		}
		if (ID) {
			try {
				await updateModel(formValues);
				showSnackBar("Updated this model!");
				navigate("/models");
			} catch (err: any) {
				showSnackBar(err?.response?.data?.message || "Failed to update model", "error");
			}
		} else {
			try {
				await createModel(formValues);
				showSnackBar("Created new model!");
				navigate("/models");
			} catch (err: any) {
				showSnackBar(err?.response?.data?.message || "Failed to create model", "error");
			}
		}
	};

	const _orderedOptionData = useMemo(() => {
		return transformOriginalOptions(transformOptions(options, true), true);
	}, [options]);

	const orderedOptionData = useMemo(() => {
		return _orderedOptionData?.filter((option: Option) => !option.is_category);
	}, [_orderedOptionData]);

	if (isLoading) return <Loader isLoading={isLoading} />;

	return (
		<Box
			sx={{ maxWidth: 720, mx: "auto", py: 3, px: 2 }}
			component="form"
			onSubmit={handleSubmit}
		>
			{/* Basic Info */}
			<Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
				<Typography variant="h6" gutterBottom>
					Basic Info
				</Typography>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
					<Input
						label="Product Name"
						size="medium"
						name="model_name"
						defaultValue={model?.model_name}
					/>
					<Input
						type="text"
						name="part_number"
						label="BF Part #"
						size="medium"
						defaultValue={model?.part_number || ""}
					/>
				</Box>
			</Paper>

			{/* Images */}
			<Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
				<Typography variant="h6" gutterBottom>
					Images
				</Typography>
				<Grid container spacing={2}>
					{logos.map((logo, index) => {
						const imageUrl = typeof logo === "string"
							? `${process.env.REACT_APP_COMPANY_LOGO_URL}/${logo}`
							: undefined;

						return (
							<Grid item xs={12} sm={4} key={index}>
								<RetryImage
									sx={{ width: "100%", height: 200 }}
									variant="square"
									src={imageUrl}
									onClick={() => handleRemoveClick(logo, index)}
									className="uploadImage"
								>
									<input
										accept="image/*"
										id={`contained-button-file-${index}`}
										type="file"
										onChange={(e) => handleUploadClick(e, index)}
										style={{ display: "none" }}
									/>
									<label htmlFor={`contained-button-file-${index}`}>
										<IconButton component="span">
											<AddIcon />
										</IconButton>
									</label>
								</RetryImage>
							</Grid>
						);
					})}
				</Grid>
			</Paper>

			{/* Pricing */}
			<Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
				<Typography variant="h6" gutterBottom>
					Pricing
				</Typography>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6}>
						<Input
							label="MSRP"
							type="number"
							defaultValue={model?.MSRP}
							name="MSRP"
							inputProps={{
								step: "any",
								min: "0",
							}}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<Input
							label={`${process.env.REACT_APP_DEALER_NAME} Price`}
							type="number"
							defaultValue={model?.dealer_price}
							name="dealer_price"
							inputProps={{
								step: "any",
								min: "0",
							}}
						/>
					</Grid>
				</Grid>
			</Paper>

			{/* Dimensions */}
			<Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
				<Typography variant="h6" gutterBottom>
					Dimensions
				</Typography>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6}>
						<Input
							label="Height"
							type="number"
							defaultValue={model?.demention_height}
							name="demention_height"
							inputProps={{
								step: "any",
								min: "0",
							}}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">"</InputAdornment>
								),
							}}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<Input
							label="Width"
							type="number"
							defaultValue={model?.demention_width}
							name="demention_width"
							inputProps={{
								step: "any",
								min: "0",
							}}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">"</InputAdornment>
								),
							}}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<Input
							label="Depth"
							type="number"
							defaultValue={model?.demention_depth}
							name="demention_depth"
							inputProps={{
								step: "any",
								min: "0",
							}}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">"</InputAdornment>
								),
							}}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<Input
							label="Rack Spaces"
							type="number"
							defaultValue={model?.demention_rack_spaces}
							name="demention_rack_spaces"
							inputProps={{
								step: "any",
								min: "0",
							}}
						/>
					</Grid>
				</Grid>
			</Paper>

			{/* Applicable Options */}
			<Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
				<Typography variant="h6" gutterBottom>
					Applicable Options
				</Typography>
				<SelectTag
					tags={tags}
					setTags={setTags}
					availableTags={orderedOptionData}
				/>
			</Paper>

			{/* Description */}
			<Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
				<Typography variant="h6" gutterBottom>
					Description
				</Typography>
				<Input
					rows={5}
					name="description"
					defaultValue={model?.description}
				/>
			</Paper>

			{/* Save Button */}
			<Box sx={{ display: "flex", justifyContent: "flex-end", mb: 4 }}>
				<Button sx={{ width: "150px" }} variant="contained" type="submit">
					Save
				</Button>
			</Box>

			<CustomDialog
				title="Are you sure you want to change this image?"
				openModal={openModal}
				handleClose={() => setOpenModal(false)}
				handleAction={handleDelete}
				buttonTitle={"Yes"}
				children={undefined}
			/>
		</Box>
	);
};
